<template>
    <pendo-modal
        :visible="isDeleteModalOpen"
        :append-to-body="true"
        class="segment-delete-confirmation-modal"
        type="confirmation"
        height="auto"
        max-height="80%"
        :title="title"
        :confirm-button-config="confirmConfig"
        :cancel-button-config="{ type: 'secondary', theme: 'app' }"
        :focus-trap-config="{ onPostDeactivate: announceAfterFocusReturn }"
        @close="handleCancel"
        @cancel="handleCancel"
        @confirm="remove">
        <template v-if="segmentsToDelete.length">
            <span class="delete-prompt">{{ deletePrompt }}</span>
            <segment-dependency-list
                :segments="segmentsToDelete"
                :segment-dependents="segmentDependents"
                :dependents-loading="dependentsLoading"
                @select-change="handleSelectChange" />
        </template>
    </pendo-modal>
</template>

<script>
import SegmentDependencyList from '../../common/segment-dependency-list/segment-dependency-list.vue';
import { PendoModal } from '@pendo/components';
import { mapActions, mapState, mapGetters } from 'vuex';
import useAriaLiveRegistry from '@/common/use/use-aria-live-registry/use-aria-live-registry';

export default {
    name: 'DeleteSegmentModal',
    components: {
        PendoModal,
        SegmentDependencyList,
    },
    props: {
        isDeleteModalOpen: {
            type: Boolean,
            default: false,
        },
        segmentsToDelete: {
            type: Array,
            required: true,
        },
    },
    emits: ['delete', 'cancel'],
    setup () {
        const { queueMessage } = useAriaLiveRegistry();

        return { queueMessage };
    },
    data () {
        return {
            localSegments: [],
            pendingAnnouncement: null,
        };
    },
    computed: {
        ...mapState({
            segmentDependents: (state) => state.segments.segmentDependents,
        }),
        ...mapGetters({
            areSegmentDependentsLoading: 'segments/areSegmentDependentsLoading',
        }),
        confirmConfig () {
            return {
                type: 'danger',
                theme: 'app',
                label: this.buttonLabel,
                disabled: this.disabled,
            };
        },
        buttonLabel () {
            if (this.attemptingToDeleteManySegments) {
                return this.$t(
                    'deleteSegmentModal.Delete Selected {segmentCount, plural, one {Segment} other {Segments}}',
                    {
                        segmentCount: this.multipleSegmentsToDelete ? this.localSegments.length : 1,
                    },
                );
            }

            return this.$t('deleteSegmentModal.Delete {segmentCount, plural, one {Segment} other {Segments}}', {
                segmentCount: this.multipleSegmentsToDelete ? this.localSegments.length : 1,
            });
        },
        multipleSegmentsToDelete () {
            return this.localSegments.length > 1;
        },
        attemptingToDeleteManySegments () {
            return this.segmentsToDelete.length > 1;
        },
        noSegmentsSelected () {
            return !this.localSegments.length && this.attemptingToDeleteManySegments;
        },
        title () {
            if (this.dependentsLoading) return this.$t('deleteSegmentModal.Checking for dependencies');

            if (this.noSegmentsSelected) {
                return this.$t('deleteSegmentModal.You have no segments currently selected to delete.');
            }

            return this.$t('deleteSegmentModal.Delete {segmentCount, plural, one {Segment} other {Segments}}?', {
                segmentCount: this.multipleSegmentsToDelete ? this.localSegments.length : 1,
            });
        },
        deletePrompt () {
            if (this.dependentsLoading) return '';

            const cannotDeleteAll = this.localSegments.length < this.segmentsToDelete.length;
            if (!cannotDeleteAll) {
                if (this.multipleSegmentsToDelete) {
                    return this.$t(
                        "deleteSegmentModal.The {numberOfSelectedSegments} selected segments will no longer be available for analytics or guides. This action can't be undone.",
                        {
                            numberOfSelectedSegments: this.localSegments.length,
                        },
                    );
                }

                return this.$t('deleteSegmentModal.segmentWillNoLongerBeAvailable', {
                    segmentName: this.segmentsToDelete[0].name,
                });
            }

            if (this.attemptingToDeleteManySegments && this.localSegments.length === 0) {
                return this.$t('deleteSegmentModal.Please reselect segments or choose "Cancel" to close.');
            }

            if (this.attemptingToDeleteManySegments) {
                return this.$t('deleteSegmentModal.Some segments are being used elsewhere and cannot be deleted.');
            }

            return this.$t('deleteSegmentModal.segmentUsedElsewhere', {
                segmentName: this.segmentsToDelete[0].name,
            });
        },
        disabled () {
            return this.localSegments.length < 1 || this.dependentsLoading;
        },
        dependentsLoading () {
            return this.areSegmentDependentsLoading(this.segmentsToDelete);
        },
    },
    watch: {
        isDeleteModalOpen (isOpen) {
            if (isOpen) return;

            this.localSegments = [];
        },
    },
    methods: {
        ...mapActions('segments', ['deleteSegment']),
        announceAfterFocusReturn () {
            if (this.pendingAnnouncement) {
                this.queueMessage(this.pendingAnnouncement);
                this.pendingAnnouncement = null;
            }
        },
        remove () {
            const count = this.localSegments.length;
            this.$emit('delete', this.localSegments);
            this.localSegments.forEach(({ id }) => {
                this.deleteSegment({ segmentId: id });
            });

            this.handleCancel();
            this.pendingAnnouncement = {
                message: this.$t('deleteSegmentModal.deletedSuccess', { count }),
                ariaLiveMode: 'polite',
            };
        },
        handleCancel () {
            this.$emit('cancel');
        },
        handleSelectChange (segments) {
            this.localSegments = segments;
        },
    },
};
</script>
<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="less">
.segment-delete-confirmation-modal {
    .pendo-modal__header {
        display: flex;
    }
}
</style>
